/**
 * HowItWorksPage.tsx — how ɳSentry works
 *
 * Purpose:    5-step explainer: status pages, incidents, SLOs, RUM, uptime checks
 * Inputs:     none (static data)
 * Outputs:    Feature sections with step numbers
 * SOT:        T-P3-E3-W2-S5-T01
 */
import { Link } from 'react-router-dom'

const sections = [
  {
    step: '01',
    title: 'Status pages your customers can actually see',
    description: 'Your infrastructure monitoring (Prometheus, Grafana) is for you. ɳSentry adds a public-facing status page so your customers know when services are up or down. Configure which checks feed each page, set maintenance windows, and subscribe users to email or webhook alerts.',
    highlights: ['Public or private status pages', 'Per-service and per-region breakdowns', 'Subscriber notifications via email or webhook', 'Maintenance window scheduling'],
  },
  {
    step: '02',
    title: 'Incident management from alert to post-mortem',
    description: 'When something breaks, ɳSentry opens an incident, pages the right person, and tracks every action until resolution. Alerts route through your preferred channels: Slack, email, PagerDuty, or webhooks. After resolution, a post-mortem template is waiting.',
    highlights: ['Auto-open incidents from uptime or SLO breach', 'Multi-channel alert routing', 'On-call rotation management', 'Post-mortem templates and timelines'],
  },
  {
    step: '03',
    title: 'SLO tracking and error budgets',
    description: 'Define the reliability targets that matter to your users — uptime percentages, latency thresholds, error rates. ɳSentry tracks your error budget in real time and alerts before you burn through it. Engineers see exactly how much reliability headroom is left.',
    highlights: ['Availability and latency SLOs', 'Error budget burn rate dashboards', 'Alert before budget is exhausted', 'Rolling window calculations (7d, 28d, 30d)'],
  },
  {
    step: '04',
    title: 'Real-user monitoring without the surveillance',
    description: 'Measure what your actual users experience: Core Web Vitals, page load times, JS errors. ɳSentry collects performance data from real sessions without cookies, third-party scripts, or data leaving your server. You see the numbers, nobody else does.',
    highlights: ['Core Web Vitals (LCP, CLS, FID/INP)', 'JS error capture with stack traces', 'No third-party scripts or cookies required', 'Data stays on your server'],
  },
  {
    step: '05',
    title: 'Uptime checks from your own infrastructure',
    description: 'HTTP and TCP uptime checks run from your VPS on a schedule you set. No external check service has access to your internal services. Scripted browser checks (via nself-synthetic-monitor) can walk multi-step flows and report on the result.',
    highlights: ['HTTP and TCP uptime checks', 'Configurable intervals and thresholds', 'Scripted browser workflow checks', 'Cron job heartbeat monitoring'],
  },
]

export function HowItWorksPage() {
  return (
    <>
      <section className="mx-auto max-w-4xl px-4 py-20 sm:px-6 lg:px-8 text-center">
        <h1 className="mb-4 text-4xl font-bold sm:text-5xl">How ɳSentry works</h1>
        <p className="text-lg text-brand-muted">Product-layer observability on top of your ɳSelf stack. Status pages, incident management, SLOs, and RUM — all self-hosted, all yours.</p>
      </section>

      <div className="pb-4">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-xl border border-emerald-200 dark:border-emerald-800/40 bg-emerald-50 dark:bg-emerald-950/20 px-6 py-4">
            <p className="text-sm text-emerald-700 dark:text-emerald-200">
              <span className="font-semibold">Free infra monitoring stays free.</span>{' '}
              Prometheus, Grafana, Loki, and the full monitoring stack ship in every ɳSelf instance at no cost.
              ɳSentry operates at the product layer, above infrastructure — these two work together, not instead of each other.
            </p>
          </div>
        </div>
      </div>

      <div className="py-4">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="divide-y divide-gray-800">
            {sections.map((s) => (
              <div key={s.step} className="grid gap-8 py-16 sm:grid-cols-2 sm:gap-16 sm:items-start">
                <div>
                  <p className="mb-3 font-mono text-xs font-semibold uppercase tracking-widest text-indigo-700 dark:text-indigo-400">{s.step}</p>
                  <h2 className="mb-4 text-2xl font-bold sm:text-3xl">{s.title}</h2>
                  <p className="text-brand-muted">{s.description}</p>
                </div>
                <div className="rounded-xl border border-brand-subtle bg-brand-surface/50 p-6">
                  <ul className="space-y-3">
                    {s.highlights.map((h) => (
                      <li key={h} className="flex items-start gap-3 text-sm text-brand-secondary">
                        <svg className="mt-0.5 h-4 w-4 flex-shrink-0 text-indigo-700 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                        </svg>
                        {h}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <section className="border-t border-brand-subtle py-16">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="mb-4 text-2xl font-bold">Want early access?</h2>
          <p className="mb-8 text-brand-muted">ɳSentry ships in v1.1.0. Join the waitlist and get an invite code when it launches.</p>
          <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
            <Link to="/invite" className="rounded-md bg-sky-500 px-8 py-3 text-base font-medium text-white shadow-lg hover:bg-sky-400 transition-colors">
              Join the waitlist
            </Link>
            <Link to="/self-host" className="rounded-md border border-brand-card px-8 py-3 text-base font-medium text-brand-secondary hover:bg-brand-surface-2 hover:text-brand-ink transition-colors">
              Self-host guide
            </Link>
          </div>
        </div>
      </section>
    </>
  )
}
